import { Injectable } from '@angular/core';
import { Permission } from '@carm/_core/role-type-permissions/models/role-type-permissions.model';
import { RoleTypePermissionService } from '@carm/_core/role-type-permissions/services';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserService } from '../facade/user.service';
import { GroupTypes, IAcl } from '../models/acl.model';
import { CarmResources } from '../models/carm-permissions';
import { ProgramTypes } from '../models/user-role.model';

@Injectable()
export class AccessControlService {

  businessProgramTypes: ProgramTypes[];

  currentProgramPermissions: Permission[];
  nativeBuinessPermissions: Permission[];

  constructor(
    protected userService: UserService,
    private roleTypePermissionService: RoleTypePermissionService
  ) {
    roleTypePermissionService
      .getAllProgramPermissions()
      .subscribe(programPermission => {
        this.currentProgramPermissions = programPermission;
      });

    roleTypePermissionService
      .getAllNativeBusinessPermissions()
      .subscribe(businessPermission => {
        this.nativeBuinessPermissions = businessPermission;
      });

    this.userService.getCurrentBusinessPrograms().subscribe(programlist => {
      this.businessProgramTypes = programlist;
    });
  }

  /**
   * Determines whether acl group and program allowed resource is
   * @param inResource
   * @param inGroupType
   * @returns
   */
  isAclGroupAndProgramAllowedResource(
    inResource: CarmResources,
    inGroupType: GroupTypes
  ): Observable<boolean> {
    return this.isAclGroupAllowedResource(inResource, inGroupType);
  }

  /**
   * Determines whether acl allowed resource is
   * @param inResource
   * @param inGroupType
   * @returns acl allowed resource
   */
  isAclGroupAllowedResource(
    inResource: CarmResources,
    inGroupType: GroupTypes,
    isNativeAcl: boolean = false
  ): Observable<boolean> {
    if (isNativeAcl) {
      return combineLatest([
          this.roleTypePermissionService.getAllNativeBusinessPermissions(),
          this.roleTypePermissionService.getAllProgramPermissions(),
          this.userService.getNativeRoles()
        ])
        .pipe(
          map(([,nativeRoles]) =>
            this.checkNativeAclGroupAllowedResource(
              inResource,
              inGroupType
            )
          )
        );
    } else {
      return combineLatest([
          this.roleTypePermissionService.getAllRolePermissions(),
          this.userService.getUserLastUsedRoleType(),
          this.roleTypePermissionService.getAllProgramPermissions(),
        ])
        .pipe(
          map((_) => this.checkAclGroupAllowedResource(inResource, inGroupType))
        );
    }
  }

  /**
   * Determines whether acl group and program allowed resource is
   * @param inResource
   * @param inGroupType
   * @returns
   */
  isAclAllowedResource(
    aclRules: IAcl[]
  ): boolean {

    for (const aclRule of aclRules) {
      if (aclRule.isNativeAcl) {
        if (
          this.checkNativeAclGroupAllowedResource(
            aclRule.resource,
            aclRule.group
          )
        ) {
          return true;
        }
      } else {
        if (
          this.checkAclGroupAllowedResource(
            aclRule.resource,
            aclRule.group
          )
        ) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Apply acl - by taking array as input and return filtered array which allowed by acl rules
   * @template T
   * @param list
   * @returns acl
   */
  applyAcl<T extends any>(list: T[]): Observable<T[]> {
    return combineLatest([
      this.userService.getUserRoles(),
      this.roleTypePermissionService.getAllProgramPermissions(),
      this.roleTypePermissionService.getAllNativeBusinessPermissions()

    ]).pipe(
      map(([userRole]) => {
        if (userRole) {
          return list.filter(ls =>
            this.applyAcl_element(ls)
          );
        } else {
          return list;
        }
      })
    );
  }

   /**
   * Determines whether acl allowed resource is
   * @param aclRules
   * @returns acl allowed resource
   */
  isAclOrphanAllowedResource(
    aclRules: { resource: string; group?: GroupTypes }[]
  ): boolean {
    return (
      aclRules &&
      aclRules.length > 0 &&
      aclRules.some(aclRule =>
        this.checkAclGroupAllowedResource(
          aclRule.resource,
          aclRule.group
        )
      )
    );
  }

  private applyAcl_element(
    element: any,
  ): boolean {
    if (element.acl) {
      const isAllowedResource = this.isAclAllowedResource(
        element.acl
      );
      return isAllowedResource;
    } else {
      return true;
    }
  }

  private checkAclGroupAllowedResource(
    inResource: string,
    inGroupType: GroupTypes
  ): boolean {

    if (this.currentProgramPermissions) {

      const resourcePermission = this.currentProgramPermissions.find(
        permission => permission.resource === inResource
      );
      if (resourcePermission && resourcePermission.group <= inGroupType) {

        return true;
      }
    }
    return false;
  }

  private checkNativeAclGroupAllowedResource(
    inResource: string,
    inGroupType: GroupTypes
  ): boolean {

    if (this.nativeBuinessPermissions) {
      const resourcePermission = this.nativeBuinessPermissions.find(
        permission => permission.resource === inResource
      );
      if (resourcePermission && resourcePermission.group <= inGroupType) {
        return true;
      }
    }
    return false;
  }

  public isCurrentCompanySuretyProvider(): Observable<boolean> {
    return this.applyAcl([{ acl: [{ resource: CarmResources.SURETY_PROVIDER, group: GroupTypes.Reader }] }])
    .pipe(map(permissions => permissions?.length > 0));
  }
}
